/**
 * Загрузка данных страницы по схеме stale-while-revalidate: сначала показываем то, что
 * уже видели, параллельно тянем свежее и молча подменяем.
 *
 * Что это даёт на практике:
 *   • возврат на страницу (Новости → Контакты → Новости) рисуется мгновенно, без спиннера;
 *   • спиннер остаётся только там, где показать реально нечего — при первом заходе;
 *   • сеть отвалилась — на экране остаются прежние данные, а не пустота с ошибкой;
 *   • вкладка вернулась из фона — данные перезапрашиваются (человек мог отсутствовать час);
 *   • два компонента с одним и тем же запросом дёргают сервер один раз (fetchShared).
 *
 * Сигнатура прежняя — { data, error, loading, reload }, — поэтому страницы менять не нужно.
 * Кеш живёт до перезагрузки вкладки и стирается при выходе из аккаунта (см. apiCache).
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { fetchShared, getCached, invalidate, isFresh, subscribe } from '@/lib/apiCache'

interface State<T> {
  data: T | null
  error: string | null
  loading: boolean
  /** Перезапросить (после изменения данных). */
  reload: () => void
}

interface Options {
  /**
   * Обновлять фоном каждые N мс, пока вкладка активна. По умолчанию выключено:
   * большинству страниц (документы, контакты, правление) это ни к чему.
   * Включать там, где данные меняются под пользователем: счета, голосования.
   */
  pollMs?: number
}

export function useApi<T>(path: string | null, options: Options = {}): State<T> {
  const { pollMs } = options

  const [data, setData] = useState<T | null>(() => (path === null ? null : getCached<T>(path) ?? null))
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(path !== null && getCached<T>(path) === undefined)

  // Живой флаг, чтобы ответ отменённого запроса не приземлился в размонтированный компонент.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  /** Сходить на сервер. force — не считаться со свежестью (нажали «обновить», правили данные). */
  const load = useCallback(
    async (key: string, force: boolean): Promise<void> => {
      if (!force && isFresh(key)) return

      const cached = getCached<T>(key)
      if (cached === undefined) setLoading(true)

      try {
        const result = await fetchShared<T>(key, () => api.get<T>(key))
        if (!alive.current) return
        setData(result)
        setError(null)
      } catch (e: unknown) {
        if (!alive.current) return
        // Данные на экране НЕ стираем: устаревшие полезнее пустого экрана.
        setError(e instanceof ApiError ? e.message : 'Ошибка загрузки')
      } finally {
        if (alive.current) setLoading(false)
      }
    },
    [],
  )

  const reload = useCallback(() => {
    if (path === null) return
    invalidate(path)
    void load(path, true)
  }, [path, load])

  // Первый заход + смена пути: показываем кеш и идём за свежим.
  useEffect(() => {
    if (path === null) {
      setData(null)
      setLoading(false)
      return
    }

    const cached = getCached<T>(path)
    setData(cached ?? null)
    setLoading(cached === undefined)
    void load(path, false)
  }, [path, load])

  // Кто-то другой обновил этот же ключ (соседний компонент, правка данных) — подхватываем.
  useEffect(() => {
    if (path === null) return
    return subscribe(path, () => {
      if (!alive.current) return
      const fresh = getCached<T>(path)
      setData(fresh ?? null)
    })
  }, [path])

  // Вкладку свернули на час и вернулись — данные наверняка устарели.
  useEffect(() => {
    if (path === null) return

    const revalidate = (): void => {
      if (document.visibilityState === 'visible') void load(path, false)
    }
    window.addEventListener('focus', revalidate)
    document.addEventListener('visibilitychange', revalidate)

    return () => {
      window.removeEventListener('focus', revalidate)
      document.removeEventListener('visibilitychange', revalidate)
    }
  }, [path, load])

  // Фоновое обновление по таймеру — только для страниц, которые об этом попросили,
  // и только пока вкладка на экране: в фоне долбить сервер незачем.
  useEffect(() => {
    if (path === null || pollMs === undefined || pollMs <= 0) return

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(path, true)
    }, pollMs)

    return () => window.clearInterval(timer)
  }, [path, pollMs, load])

  return { data, error, loading, reload }
}
